import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../../../../components/src/Button";
import { OutputFileThumbnail } from "./OutputFileThumbnail";
import { FundParseOutputFile, FundParseOutputManifest, FundParseOutputManifestGroup } from "./fundParseTypes";
import {
    buildLegacyOutputGroups,
    downloadAllOutputFiles,
    isThumbnailOutputFile,
    resolveManifestGroupFiles
} from "./fundParseUtils";

interface FundParseOutputPanelProps {
    outputFiles: FundParseOutputFile[];
    manifest: FundParseOutputManifest | null;
    isRunning: boolean;
    onSelectFile: (file: FundParseOutputFile) => void;
    onClear: () => void;
}

function OutputGroupSection({
    group,
    outputFiles,
    onSelectFile
}: {
    group: FundParseOutputManifestGroup;
    outputFiles: FundParseOutputFile[];
    onSelectFile: (file: FundParseOutputFile) => void;
}): React.ReactElement | null {
    const [expanded, setExpanded] = useState(group.defaultExpanded);
    const groupFiles = useMemo(() => resolveManifestGroupFiles(group, outputFiles), [group, outputFiles]);
    const thumbnailFiles = useMemo(
        () => groupFiles.filter((file) => isThumbnailOutputFile(file.fileName)),
        [groupFiles]
    );
    const otherFiles = useMemo(
        () => groupFiles.filter((file) => !isThumbnailOutputFile(file.fileName)),
        [groupFiles]
    );

    if (!groupFiles.length) {
        return null;
    }

    return (
        <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <button
                    type="button"
                    className="flex items-center gap-2 text-left text-base font-medium text-gray-900"
                    onClick={() => setExpanded((current) => !current)}
                >
                    {expanded ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                    <span>{group.label}</span>
                    <span className="text-sm font-normal text-gray-500">({groupFiles.length})</span>
                </button>
                <Button variant="secondary" size="sm" onClick={() => downloadAllOutputFiles(groupFiles)} disabled={!groupFiles.length}>
                    Download group
                </Button>
            </div>

            {expanded ? (
                <>
                    {thumbnailFiles.length ? (
                        <div className="mb-3 grid [grid-template-columns:repeat(auto-fill,minmax(10rem,1fr))] gap-4">
                            {thumbnailFiles.map((file) => {
                                const manifestEntry = group.files.find((entry) => entry.fileName === file.fileName);
                                return (
                                    <div key={file.fileName} className="min-w-0">
                                        <OutputFileThumbnail
                                            file={file}
                                            label={manifestEntry?.label}
                                            onClick={onSelectFile}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}

                    {otherFiles.length ? (
                        <ul className="space-y-1 text-sm text-gray-700">
                            {otherFiles.map((file) => {
                                const manifestEntry = group.files.find((entry) => entry.fileName === file.fileName);
                                return (
                                    <li key={file.fileName}>
                                        <button type="button" className="text-sky-600 hover:underline" onClick={() => onSelectFile(file)}>
                                            {manifestEntry?.label || file.fileName}
                                        </button>
                                        {manifestEntry && manifestEntry.label !== file.fileName ? (
                                            <span className="ml-2 text-xs text-gray-500">{file.fileName}</span>
                                        ) : null}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}

                    {!thumbnailFiles.length && !otherFiles.length ? (
                        <p className="text-sm text-gray-500">No files in this group.</p>
                    ) : null}
                </>
            ) : null}
        </section>
    );
}

export function FundParseOutputPanel({
    outputFiles,
    manifest,
    isRunning,
    onSelectFile,
    onClear
}: FundParseOutputPanelProps): React.ReactElement {
    const groups = useMemo(() => {
        if (manifest?.groups?.length) {
            return manifest.groups;
        }
        return buildLegacyOutputGroups(outputFiles);
    }, [manifest, outputFiles]);

    return (
        <div className="mt-8 rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className="text-lg font-medium">Output files</h2>
                    {manifest?.valuationDate ? (
                        <p className="text-sm text-gray-500">Valuation date: {manifest.valuationDate}</p>
                    ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" size="sm" onClick={onClear} disabled={!outputFiles.length || isRunning}>
                        Clear output files
                    </Button>
                </div>
            </div>

            {groups.length ? (
                <div className="space-y-4">
                    {groups.map((group) => (
                        <OutputGroupSection key={group.id} group={group} outputFiles={outputFiles} onSelectFile={onSelectFile} />
                    ))}
                </div>
            ) : (
                <p className="text-sm text-gray-500">No output files yet.</p>
            )}
        </div>
    );
}
